import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import InstallButton from './downloadbutton';
import BackgroundLogins from './backgroundlogins';

// Dynamic API base URL based on environment
const API_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://api.insightos.co.uk'   // your production API
    : 'https://api.insightos.co.uk';

export default function Authentication() {
  const location = useLocation();
  const { token = '' } = useParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [verifyPasswordVisible, setVerifyPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // Pre-fill email from location state (if coming from sign-up)
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  // Step 1: Verify email token
  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!token) {
      setErrorMessage('This verification link is missing a token. Please request a new verification email.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, Email: email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsVerified(true);
        setLoginEmail(email);
        setCompanyCode(data.companyCode); // must be returned by backend
      } else {
        setErrorMessage(data?.message || 'Verification failed. Please request a new verification link.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setErrorMessage('Failed to verify link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Set company password
  const handlePassword = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (password !== verifyPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/companypassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Email: loginEmail,
          Password: password,
          CompanyCode: companyCode,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Password set successfully → redirect to login or dashboard
        navigate('/', { replace: true });
      } else {
        setErrorMessage(data?.message || 'Failed to set password. Please try again.');
      }
    } catch (error) {
      console.error('Password setting error:', error);
      setErrorMessage('Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-8 bg-slate-950">
      <style>{`
        @keyframes verificationFloatA {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(36px, -42px); }
        }
        @keyframes verificationFloatB {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(-30px, 36px); }
        }
      `}</style>
      <div className="pointer-events-none absolute -top-28 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-3xl" style={{ animation: 'verificationFloatA 24s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute -bottom-24 -right-20 h-[380px] w-[380px] rounded-full bg-violet-600/20 blur-3xl" style={{ animation: 'verificationFloatB 28s ease-in-out infinite' }} />
      <BackgroundLogins />
      <InstallButton />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/75 px-6 py-7 shadow-2xl backdrop-blur-xl sm:px-8">
        <div className="mb-6 text-center">
          <img className="mx-auto h-14 w-14 drop-shadow-lg" src="/1.png" alt="InsightOS" loading="eager" />
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white">
            {isVerified ? 'Set Your Password' : 'Account Verification'}
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            {isVerified
              ? `Create a password for ${loginEmail} (${companyCode})`
              : 'Use your verification link and email to activate your account.'}
          </p>
        </div>

        {!token && !isVerified && (
          <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            This verification link is invalid. Please request a new verification email.
          </div>
        )}

        {!isVerified ? (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-white">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
              />
            </div>

            {errorMessage && (
              <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                loading || !token
                  ? 'cursor-not-allowed bg-indigo-500/40'
                  : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
              }`}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handlePassword}>
            <div className="flex items-center justify-between">
              <p className="text-slate-300 text-xs">Email: <span className="font-mono">{loginEmail}</span></p>
              <p className="text-slate-300 text-xs">Code: <span className="font-mono font-bold text-indigo-300">{companyCode}</span></p>
            </div>
            

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-white">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={passwordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-slate-400"
                >
                  {passwordVisible ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="verifyPassword" className="mb-2 block text-sm font-medium text-white">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="verifyPassword"
                  name="verifyPassword"
                  type={verifyPasswordVisible ? 'text' : 'password'}
                  value={verifyPassword}
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                />
                <button
                  type="button"
                  onClick={() => setVerifyPasswordVisible(!verifyPasswordVisible)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs text-slate-400"
                >
                  {verifyPasswordVisible ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                loading
                  ? 'cursor-not-allowed bg-indigo-500/40'
                  : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
              }`}
            >
              {loading ? 'Setting password...' : 'Set Password & Login'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}